import { Avatar, AvatarGroup, Box, Card, IconButton, Paper, Popper, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import dayjs from 'dayjs'

import { formatDate, getTimePassedFrom, Icons } from '../../../utils/utilities'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import { useSimbaAIStore } from '../../../store/simbaai.store'
import sadSimba from '../../../assets/simba-ai/practicing.png'
import simbaImg from '../../../assets/simba-ai/celebration.png'
import CustomChip from '../../common/CustomChip'
import LeaderboardDialog from '../../ai-mentor/LeaderboardDialog'

const StudentPracticeWithSimbaCard = () => {

    const [showStreakMaintenanceInfo, setShowStreakMaintenanceInfo] = useState( false )
    const [leaderboardDialogOpen, setLeaderboardDialogOpen] = useState( false )
    const [loading, setLoading] = useState( true )

    const { palette, border, layout, shadows, theme } = useTheme()
    const setSearchParams = useSearchParams().at( 1 )

    const SimbaAIStore = useSimbaAIStore()

    const streakInfoRef = useRef()

    const streakHeadingStyles = useMemo( () => ( {
        background: 'linear-gradient(90deg, #ff6a00, #ee0909ff)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        lineHeight: 1.1,
        fontWeight: "700",
        color: ( theme === 'dark' ) ? 'transparent' : palette.common.white,
        fontSize: { sm: "14px", xs: "12px" },
    } ), [palette, theme] )

    const headingEmojiStyles = useMemo( () => ( {
        background: "none",
        fontSize: "inherit",
        fontWeight: "inherit",
        WebkitBackgroundClip: 'initial',
        WebkitTextFillColor: 'initial',
        backgroundClip: 'initial',
        color: '#ff6a00',
    } ), [] )

    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                newParams.set( key, values[key] )
            }
            return newParams
        }, { replace: true } )
    }

    const startQuiz = async ( practiceSession ) => {
        setSearchParamsValues( { simbaAIOpen: 'true', practiceQuiz: practiceSession._id, simbaAIMode: "practice" } )

        SimbaAIStore.setView( "loading" )
        const { success, data } = await SimbaAIStore.fetchPracticeSessionDetails( practiceSession._id )

        if ( success )
            if ( data.incomplete_session_exists ) {
                SimbaAIStore.setView( "continue-pending", "left", false )
            } else {
                if ( data.practiced ) {
                    SimbaAIStore.setView( "start-again", "left", false )
                } else {
                    const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceSession._id )
                    if ( success ) {
                        SimbaAIStore.setView( "question", "left", false )
                        try {
                            const quizContainer = document.getElementById( "simba-quiz-container" )
                            if ( !window.document.fullscreenElement ) {
                                if ( quizContainer ) {
                                    if ( quizContainer.requestFullscreen ) {
                                        await quizContainer.requestFullscreen()
                                    } else if ( quizContainer.webkitRequestFullscreen ) {
                                        // Safari
                                        await quizContainer.webkitRequestFullscreen()
                                    } else if ( quizContainer.msRequestFullscreen ) {
                                        // IE/Edge
                                        await quizContainer.msRequestFullscreen()
                                    }
                                }
                            }
                        } catch ( err ) {

                        }
                    }
                }
            }

    }

    const getPracticeSessions = useCallback( async () => {
        setLoading( true )
        SimbaAIStore.fetchLatestPracticeStreak()
        await Promise.all( [
            SimbaAIStore.fetchPracticeSessions(),
            SimbaAIStore.fetchUserHistory(),
        ] )
        setLoading( false )
    }, [SimbaAIStore] )

    useEffect( () => {
        getPracticeSessions()
    }, [getPracticeSessions] )

    return (
        <Box gridArea="pwsb" display="flex" flexDirection="column">
            <Box marginBottom="5px" display="flex" gap="10px" alignItems="center">
                <Typography display="flex" gap="5px" alignItems="center" variant='subtitle2' component="h3" flexGrow={1}>
                    <Typography component="span" display="flex" alignItems="center" color='primary'>{Icons.default.AskAI}</Typography>
                    Recent Practice Sessions
                    {/* <CountBadge value={SimbaAIStore.getPracticeSessions.slice( 0, 3 ).length} /> */}
                </Typography>

                <Box sx={{ cursor: "pointer" }}>

                    <Popper placement='bottom-start' className='streak-maintaining-info' anchorEl={streakInfoRef.current} open={showStreakMaintenanceInfo}  >
                        <Paper sx={{ overflow: "clip", border: border[1], marginTop: "10px" }}>
                            <Box bgcolor={palette.primary.dark + "99"} maxWidth="400px" >
                                <Typography padding="5px 10px" borderBottom={`1px solid ${palette.common.white}`} variant='subtitle2' color={palette.primary.contrastText} component="p">How to Maintain Your Streak?</Typography>
                                <Typography padding="5px 10px" fontSize="12px" variant='body2' color={palette.primary.contrastText} component="p">A streak represents the number of consecutive days you have engaged in practice. To maintain and advance your streak, ensure you complete and submit at least one practice session every 24 hours.</Typography>
                            </Box>
                        </Paper>
                    </Popper>

                    <Box
                        onMouseLeave={() => {
                            setShowStreakMaintenanceInfo( false )
                        }}
                        onMouseEnter={() => {
                            setShowStreakMaintenanceInfo( true )
                        }}
                        ref={streakInfoRef}
                    >
                        {!SimbaAIStore.getLatestPracticeStreak.streakExists && <Typography textAlign="right" sx={streakHeadingStyles}>Start your first streak today <Typography component="span" fontSize="inherit" lineHeight={1.1} sx={headingEmojiStyles} fontWeight="inherit" >🚀</Typography></Typography>}
                        {SimbaAIStore.getLatestPracticeStreak.streakExists && !SimbaAIStore.getLatestPracticeStreak.activeStreak && <Typography textAlign="right" sx={streakHeadingStyles}>Oops! Your streak has fallen asleep <Typography component="span" fontSize="inherit" lineHeight={1.1} sx={headingEmojiStyles} fontWeight="inherit" >😴</Typography></Typography>}
                        {SimbaAIStore.getLatestPracticeStreak.streakExists && SimbaAIStore.getLatestPracticeStreak.activeStreak && SimbaAIStore.getLatestPracticeStreak.todaysStreakCompleted && <Typography textAlign="right" sx={streakHeadingStyles}>Amazing! You’re on a {SimbaAIStore.getLatestPracticeStreak?.latestStreak?.streak?.length}-day streak <Typography component="span" fontSize="inherit" lineHeight={1.1} sx={headingEmojiStyles} fontWeight="inherit" >🔥</Typography></Typography>}
                        {SimbaAIStore.getLatestPracticeStreak.streakExists && SimbaAIStore.getLatestPracticeStreak.activeStreak && !SimbaAIStore.getLatestPracticeStreak.todaysStreakCompleted && <Typography textAlign="right" sx={streakHeadingStyles}>Your streak is counting on you <Typography component="span" fontSize="inherit" lineHeight={1.1} sx={headingEmojiStyles} fontWeight="inherit" >⏳</Typography></Typography>}
                    </Box>
                </Box>

            </Box>

            <Card className='custom-scrollbar' elevation={0} sx={{ flexGrow: 1, padding: "5px", overflow: "auto", border: border[1], display: "flex", flexDirection: "column" }}>

                {/* {SimbaAIStore.getPracticeSessions.length > 0 && <LatestPracticeStreak parent="recentSessionsCard" />} */}

                <LeaderboardDialog open={leaderboardDialogOpen} setOpen={setLeaderboardDialogOpen} />

                <Box onClick={() => setLeaderboardDialogOpen( true )} alignItems="center" display="flex" gap={layout.cardsGap} sx={{ cursor: "pointer", background: `linear-gradient(135deg, ${palette.customThemeColor.light + 44}, transparent)`, ":hover": { borderColor: palette.primary.main } }} border={border[1]} borderRadius={layout.borderRadius} padding="10px 10px">


                    <Box component="img" src={simbaImg} height="50px" />
                    <Box flexGrow={1}>
                        {/* <Typography lineHeight={1.3} variant='h6' fontSize="16px">Simba AI is here!</Typography>
                        <Typography variant='subtitle2' fontSize="12px">Practice the concepts learnt in your classes with the help of Simba AI.</Typography> */}
                        <Typography lineHeight={1.3} variant='h6' fontSize="16px">Simba AI Monthly Leaderboard</Typography>
                        <Typography variant='subtitle2' fontSize="12px">Check who is leading the preactice race in your branch!</Typography>
                    </Box>
                    <Box display="flex" >
                        <Typography position="relative" zIndex={5} right="-15px" bottom="-10px" fontSize="2rem" color='silver' >{Icons.default.EmojiEvents}</Typography>
                        <Typography position="relative" zIndex={10} fontSize="2.5rem" color='gold'>{Icons.default.EmojiEvents}</Typography>
                        <Typography position="relative" zIndex={5} left="-15px" bottom="-10px" fontSize="2rem" color='brown' >{Icons.default.EmojiEvents}</Typography>
                    </Box>
                    <IconButton size='small'>
                        {Icons.default.ChevronRightIcon}
                    </IconButton>
                </Box>


                {SimbaAIStore.getPracticeSessions.filter( ps => dayjs().diff( dayjs( ps.created_at ), 'days' ) < 50 && ( !ps.close_date || dayjs( ps.close_date ) > dayjs() ) ).length > 0 && <Box marginTop="10px" display="flex" flexDirection="column" gap="5px">
                    {SimbaAIStore.getPracticeSessions.filter( ps => dayjs().diff( dayjs( ps.created_at ), 'days' ) < 50 && ( !ps.close_date || dayjs( ps.close_date ) > dayjs() ) ).slice( 0, 5 ).map( practiceSession => {

                        let completed = false
                        const topics = Object.values( practiceSession.modules_and_topics ).map( mod => mod.topics ).flat()
                        const practiced = SimbaAIStore.getPracticeHistory.find( item => item.practice_session._id === practiceSession._id )

                        if ( practiced && practiced.status === "completed" )
                            completed = true

                        return (
                            <Box display="flex" flexDirection={{ xs: "column-reverse", lg: "row" }} alignItems="flex-start" gap={layout.cardsGap} justifyContent="space-between" position="relative" onClick={() => startQuiz( practiceSession )} sx={{ cursor: "pointer", ":hover": { boxShadow: shadows[1] } }} padding="10px" borderRadius="5px" border={border[1]} key={practiceSession._id}>
                                <Box>
                                    <Box color={palette.text.secondary} display="flex" gap="5px" fontSize="14px" flexWrap="wrap">
                                        <CustomChip tooltipTitle='Practice difficulty level' value={practiceSession.complexity} />
                                        {!practiced && <Typography fontSize="10px" borderRadius="10px" fontWeight="500" color={practiceSession.status === 'draft' ? "error.contrastText" : "error.contrastText"} bgcolor={practiceSession.status !== 'draft' ? palette.error.main : palette.error.main} padding="3px 5px" >Yet to attempt</Typography>}
                                        {!practiced && <Typography className='animation-wave' fontSize="10px" borderRadius="10px" variant='subtitle2' sx={{ borderRadius: "10px", color: palette.primary.contrastText, bgcolor: palette.primary.main }} padding="3px 5px" >NEW</Typography>}
                                        {completed && <Typography fontSize="10px" borderRadius="10px" fontWeight="500" color={practiceSession.status === 'draft' ? "success.contrastText" : "success.contrastText"} bgcolor={practiceSession.status !== 'draft' ? palette.success.main : palette.success.main} padding="3px 5px" >Completed practice</Typography>}
                                        {practiced && !completed && <Typography className='animation-wave' fontSize="10px" borderRadius="10px" fontWeight="500" bgcolor={palette.contentBg} padding="3px 5px" >Pending completion</Typography>}
                                        {practiced && completed && <Typography fontSize="10px" borderRadius="10px" variant='subtitle2' sx={{ background: palette.primary.main, borderRadius: "10px", color: palette.common.white }} padding="3px 5px" >Score: {practiced.score} / {practiced.questions.length}</Typography>}
                                    </Box>
                                    <Typography variant='h6' component="p" fontSize="16px" >{practiceSession.title}</Typography>
                                    <Box color={palette.text.secondary} minWidth={0} display="flex" gap="5px" fontSize="14px" alignItems="center">
                                        {Icons.default.Subjects}
                                        <Typography variant='subtitle2' minWidth={0} noWrap color='textSecondary' fontSize="12px" >{practiceSession.subject?.subject_name} - {practiceSession.subject?.subject_code}</Typography>
                                    </Box>
                                    <Box marginTop="2px" flexWrap="wrap" color={palette.text.secondary} display="flex" gap="5px" fontSize="14px" alignItems="center">
                                        {topics.slice( 0, 2 ).map( ( topic, index ) => (
                                            <CustomChip key={topic} value={topic} />
                                        ) )}
                                        {topics.length > 2 && <CustomChip value={`+${topics.length - 2} More`} />}
                                    </Box>
                                </Box>

                                <Box display="flex" flexDirection="column" justifyContent="space-between" gap={layout.cardsGap} alignItems="flex-end">
                                    <Box display="flex" height="fit-content" alignItems="flex-start" maxWidth="250px" gap="5px" flexWrap="wrap" justifyContent="flex-end">
                                        <CustomChip tooltipTitle='Created' startIcon={Icons.default.Clock} value={getTimePassedFrom( practiceSession.created_at )} />
                                        {practiceSession.close_date && <CustomChip textVariant="subtitle2" startIcon={Icons.default.Calendar} value={`Closes on: ${formatDate( practiceSession.close_date )}`} tooltipTitle="Ends Date" />}
                                    </Box>
                                    {/* <Button endIcon={Icons.default.KeyBoardArrowRight} sx={{ borderBottom: `4px solid ${palette.primary.light + "99"}`, borderRadius: "10px" }} startIcon={Icons.default.Logout} variant='contained' disableElevation color='primary'>
                                        Start Practice
                                    </Button> */}
                                </Box>
                            </Box>
                        )
                    } )}

                </Box>}

                {!loading && SimbaAIStore.getPracticeSessions.filter( ps => dayjs().diff( dayjs( ps.created_at ), 'days' ) < 50 && ( !ps.close_date || dayjs( ps.close_date ) > dayjs() ) ).length === 0 && <Box flexGrow={1} display="flex" alignItems="center" padding="20px" flexDirection="column" gap="10px" justifyContent="center" >
                    <img src={sadSimba} height="130px" alt="No practice sessions in last 50 days!" />
                    <Typography textAlign="center" marginTop="20px" variant="subtitle2" >No practice sessions to show!</Typography>
                </Box>}
                {loading && <Box flexGrow={1} display="flex" alignItems="center" padding="20px" height="100%" flexDirection="column" gap="10px" justifyContent="center" >
                    <DataLoadingSpinner waitingMessage='Loading practice sessions...' />
                </Box>}

            </Card>
        </Box>
    )
}

export default StudentPracticeWithSimbaCard
